/**
 * Mirrors of the C# Logistics › Inventory DTO records, field for field.
 *
 * Five modules live here because the rail groups them as one — Items and Storage Facilities are
 * where the work happens, and the three vocabularies below them are what that work draws on.
 * They keep separate permission keys and separate `<IndexPage>` module keys; only the folder is
 * shared, the way `organization/` holds the tier ladder and `plantilla/` holds the salary
 * schedule.
 */

/** Enums serialize as int on the wire, so the numbers are the contract. */
export const ITEM_KIND = {
  consumable: 0,
  asset: 1,
  subscription: 2,

  /**
   * Land, a building or a leased space — declared 20 Sep 2026.
   *
   * It holds no stock and is never issued. Its catalog row is the TYPE ("Office Space") and
   * the actual one is a row in the real-property REGISTER, which is the same two stages a
   * serialized asset takes.
   */
  realProperty: 3,
} as const;

export type InventoryItemKind = (typeof ITEM_KIND)[keyof typeof ITEM_KIND];

export const LICENSING_MODEL = {
  perpetual: 0,
  term: 1,
} as const;

export type LicensingModel = (typeof LICENSING_MODEL)[keyof typeof LICENSING_MODEL];

export const OWNERSHIP = {
  owned: 0,
  leased: 1,
} as const;

export type OwnershipType = (typeof OWNERSHIP)[keyof typeof OWNERSHIP];

/**
 * How a licence seat is PAID FOR — the billing cadence, not the price.
 *
 * An enum rather than a catalog, on the test this suite already sets: each member changes what
 * the system DOES. `isRecurringPaymentMode` is the one place that shows — it decides whether a
 * first-billing date is asked for at all, and the server refuses and clears one that arrives
 * beside a mode that bills once.
 */
export const LICENSE_PAYMENT_MODE = {
  oneTime: 0,
  monthly: 1,
  quarterly: 2,
  semiAnnual: 3,
  annual: 4,
} as const;

export type LicensePaymentMode =
  (typeof LICENSE_PAYMENT_MODE)[keyof typeof LICENSE_PAYMENT_MODE];

/** One label per kind, in one place — the list, the profile and the form all read it. */
export const ITEM_KIND_LABEL: Record<InventoryItemKind, string> = {
  // 'Expendables', not the reference's 'Consumables' — product call, 29 Sep 2026. The KEY
  // stays `consumable` for the reason the enum member does: an identifier is not a label.
  [ITEM_KIND.consumable]: 'Expendables',
  [ITEM_KIND.asset]: 'Equipment',
  [ITEM_KIND.subscription]: 'Digital & Subscriptions',

  // 'Real Property', never 'Property' — this product already uses that word for a field on a
  // record, and one word for two things is what the naming rule refuses.
  [ITEM_KIND.realProperty]: 'Real Property',
};

/** What each kind means, for the form's help text. Kept beside the labels so they cannot drift. */
export const ITEM_KIND_DESCRIPTION: Record<InventoryItemKind, string> = {
  [ITEM_KIND.consumable]: 'Used up when handed over. Tracked by batch, never given back.',
  [ITEM_KIND.asset]: 'Stays on the books after it leaves the store. Assigned or lent, and returnable.',
  [ITEM_KIND.subscription]: 'A licence or recurring service. Tracked per seat, because each one expires on its own date.',
  [ITEM_KIND.realProperty]: 'Land, a building or a leased space. Each actual one is registered separately, with its own address and terms.',
};

export const LICENSING_MODEL_LABEL: Record<LicensingModel, string> = {
  [LICENSING_MODEL.perpetual]: 'Perpetual',
  [LICENSING_MODEL.term]: 'Term',
};

export const OWNERSHIP_LABEL: Record<OwnershipType, string> = {
  [OWNERSHIP.owned]: 'Owned',
  [OWNERSHIP.leased]: 'Leased',
};

export const LICENSE_PAYMENT_MODE_LABEL: Record<LicensePaymentMode, string> = {
  [LICENSE_PAYMENT_MODE.oneTime]: 'One-time',
  [LICENSE_PAYMENT_MODE.monthly]: 'Monthly',
  [LICENSE_PAYMENT_MODE.quarterly]: 'Quarterly',
  [LICENSE_PAYMENT_MODE.semiAnnual]: 'Semi-annual',
  [LICENSE_PAYMENT_MODE.annual]: 'Annual',
};

/**
 * Whether the licence is invoiced again after the first payment.
 *
 * The client half of `LicensePaymentModes.IsRecurring`, and the two must agree: the server
 * refuses a first-billing date beside a mode that bills once, so a form that offered the cell
 * anyway would collect a value the save then rejects.
 */
export function isRecurringPaymentMode(mode: LicensePaymentMode): boolean {
  return mode !== LICENSE_PAYMENT_MODE.oneTime;
}

// ── Categories ───────────────────────────────────────────────────────────────────────────

export interface InventoryCategoryListItem {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  /** Position in the list. Server-assigned in gaps of ten; the form never asks for it. */
  sortOrder: number;
  /** The kind the item form pre-selects. Null leaves the form asking. */
  defaultKind: InventoryItemKind | null;
  /** Live count of active items filed under it. Derived server-side, never stored. */
  itemCount: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface InventoryCategoryOption {
  id: string;
  name: string;
  /** On the option so the item form can pre-select the kind without a second request. */
  defaultKind: InventoryItemKind | null;
}

export interface InventoryCategoryRequest {
  name: string;
  code: string | null;
  description: string | null;
  defaultKind: InventoryItemKind | null;
}

// ── Item types ───────────────────────────────────────────────────────────────────────────

export interface InventoryItemTypeListItem {
  id: string;
  name: string;
  description: string | null;
  /** Null means the name is offered under every category — the deliberate default. */
  inventoryCategoryId: string | null;
  /** Resolved on every read. There is no snapshot column and there must not become one. */
  inventoryCategoryName: string | null;
  itemCount: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface InventoryItemTypeOption {
  id: string;
  name: string;
  /** The item form narrows its picker on this as the reader changes category. */
  inventoryCategoryId: string | null;
}

export interface InventoryItemTypeRequest {
  name: string;
  description: string | null;
  inventoryCategoryId: string | null;
}

// ── Units of measure ─────────────────────────────────────────────────────────────────────

export interface UnitOfMeasureListItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  itemCount: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface UnitOfMeasureOption {
  id: string;
  code: string;
  name: string;
}

export interface UnitOfMeasureRequest {
  code: string;
  name: string;
  description: string | null;
}

// ── Items ────────────────────────────────────────────────────────────────────────────────

export interface InventoryItemListItem {
  id: string;
  sku: string;
  /** The governed name, resolved through the item type on every read. */
  name: string;
  description: string | null;
  inventoryCategoryId: string;
  inventoryCategoryName: string;
  kind: InventoryItemKind;
  unitCode: string;
  reorderLevel: number;
  maxStockLevel: number | null;
  tracksSerial: boolean;
  tracksExpiry: boolean;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** The whole item, for its profile page. */
export interface InventoryItemDetail {
  id: string;
  sku: string;
  inventoryItemTypeId: string;
  name: string;
  inventoryCategoryId: string;
  inventoryCategoryName: string;
  kind: InventoryItemKind;
  unitOfMeasureId: string;
  unitCode: string;
  unitName: string;
  description: string | null;
  reorderLevel: number;
  maxStockLevel: number | null;
  tracksExpiry: boolean;
  expiryThresholdDays: number;
  estimatedUsefulLifeYears: number | null;
  tracksSerial: boolean;
  manufacturer: string | null;
  brandName: string | null;
  model: string | null;
  licensingModel: LicensingModel | null;
  ownership: OwnershipType | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * One selectable item, for a stock form's picker.
 *
 * `GET /api/inventory-items/options` has existed since the module landed and nothing called it
 * until the stock forms arrived — the catalog pages all read the paged list. It carries
 * `tracksSerial` because every stock form branches on it the moment an item is chosen: a
 * serialized item is recorded as units and a quantity one as lots, and the form has only the
 * options list to hand.
 */
export interface InventoryItemOption {
  id: string;
  sku: string;
  name: string;
  unitCode: string;
  tracksSerial: boolean;
  tracksExpiry: boolean;

  /**
   * What kind of thing it is.
   *
   * On the option for the reason `tracksSerial` is: a form narrows the list the moment it
   * opens and has only the options to hand. The property form is the first caller — only a
   * real-property type can parent a property.
   */
  kind: InventoryItemKind;
}

export interface InventoryItemRequest {
  /**
   * The stock-keeping code. NULL on a digital asset, where the server assigns `LIC-{yy}{NNN}`,
   * and on REAL PROPERTY, where it assigns `PROP-{yy}{NNN}`.
   *
   * An EDIT still carries the stored value back, whatever the kind: a form that cannot see a
   * field must not be able to destroy it, and a blank on an update keeps what is recorded
   * rather than re-issuing a code a shelf label already quotes.
   */
  sku: string | null;

  /**
   * The catalog name. NULL on REAL PROPERTY, where the server resolves the seeded `Real
   * Property` row — a property has no item type below its own kind, so the form does not ask.
   *
   * Every other kind names one, and a null from those is refused rather than resolved.
   */
  inventoryItemTypeId: string | null;
  inventoryCategoryId: string;
  kind: InventoryItemKind;
  /**
   * What stock is counted in. NULL on a digital asset, where the server resolves the seeded
   * `lic` unit — a licence is counted in licences, so there is nothing for the form to ask.
   */
  unitOfMeasureId: string | null;
  description: string | null;
  reorderLevel: number;
  maxStockLevel: number | null;
  tracksExpiry: boolean;
  expiryThresholdDays: number;
  estimatedUsefulLifeYears: number | null;
  tracksSerial: boolean;
  manufacturer: string | null;
  /** Consumable only. Refused on any other kind. */
  brandName: string | null;
  /** Asset and Subscription only. Refused on a consumable. */
  model: string | null;
  /** Subscription only. */
  licensingModel: LicensingModel | null;
  /** Asset only. */
  ownership: OwnershipType | null;
}

// ── Storage facilities ───────────────────────────────────────────────────────────────────

export interface StorageFacilityListItem {
  id: string;
  /** `FAC-{yy}{NNN}` — allocated by the server, permanent, and absent from the request. */
  code: string;
  name: string;
  facilityType: string | null;
  parentFacilityId: string | null;
  parentFacilityName: string | null;
  orgUnitId: string | null;
  /** The owning department, resolved on every read — never a stored copy. */
  orgUnitName: string | null;
  notes: string | null;
  childCount: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface StorageFacilityOption {
  id: string;
  code: string;
  name: string;
  /** "Main Warehouse, Bay 2, Shelf A" — composed server-side, so two surfaces cannot disagree. */
  path: string;
}

/**
 * <b>There is no `code` on it.</b> A facility's code is `FAC-{yy}{NNN}`, allocated by the
 * server and permanent — a position is assigned, never asked for — so the form has no field for
 * it and this type has no property the form could fill.
 */
export interface StorageFacilityRequest {
  name: string;
  facilityType: string | null;
  parentFacilityId: string | null;
  orgUnitId: string | null;
  notes: string | null;
}
